/**
 * Modal shell: корректировка уже применённого ИИ-плана из sidebar «Планы МИС».
 * Переиспользует PlanDiagnosticsReviewPanel (уточнение / JSON / применение).
 */
import { useEffect, useState } from 'react'
import type { AuthSession } from '../../../types/plans'
import type { MisImportedPlanSummary } from './auto-distribute/selectedPlanServices'
import { misImportedPlanDateLabel, misImportedPlanDoctorLabel } from './auto-distribute/selectedPlanServices'
import { PlanDiagnosticsReviewPanel } from './diagnostics/PlanDiagnosticsReviewPanel'
import { buildSeedPayloadFromMisPlan } from './diagnostics/misPlanCorrectionPayload'
import {
  PLAN_DIAGNOSTICS_MODEL_CHOICE,
  seedMisPlanCorrectionProposal,
  type DiagnosticsAiProposal,
} from './diagnostics/planDiagnosticsCompose'

const UI_SURFACE = 'staff_lk_mis_plan_correction'

function extractMisPlanId(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null
  const p = payload as Record<string, unknown>
  const apply = p.apply_result ?? p.result ?? p
  if (!apply || typeof apply !== 'object') return null
  const a = apply as Record<string, unknown>
  if (typeof a.target_entity_id === 'string') return a.target_entity_id
  const created = a.created_plan
  if (created && typeof created === 'object' && typeof (created as { plan_id?: string }).plan_id === 'string') {
    return (created as { plan_id: string }).plan_id
  }
  return null
}

export function MisPlanCorrectionShell({
  apiBase,
  session,
  misPatientId,
  staffUserId,
  planSummary,
  onClose,
  onApplied,
}: {
  apiBase: string
  session: AuthSession
  misPatientId: string
  staffUserId: string
  planSummary: MisImportedPlanSummary
  onClose: () => void
  onApplied: (misPlanId: string | null) => void
}) {
  const authHeader = { Authorization: `Bearer ${session.accessToken}` }
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [proposal, setProposal] = useState<DiagnosticsAiProposal | null>(null)

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      setLoading(true)
      setError('')
      try {
        const res = await fetch(
          `${apiBase}/staff/mis/treatment-plans/${encodeURIComponent(planSummary.plan_id)}`,
          { headers: authHeader },
        )
        if (!res.ok) throw new Error(`plan HTTP ${res.status}`)
        const data = (await res.json()) as { plan?: Parameters<typeof buildSeedPayloadFromMisPlan>[0] }
        if (!data.plan) throw new Error('plan_not_found')
        const seedPayload = buildSeedPayloadFromMisPlan(data.plan, staffUserId)
        const seeded = await seedMisPlanCorrectionProposal({
          apiBase,
          authHeader,
          misPatientId,
          staffUserId,
          sourceMisPlanId: planSummary.plan_id,
          seedPayload,
        })
        if (!cancelled) setProposal(seeded)
      } catch (e) {
        if (!cancelled) setError(String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [apiBase, authHeader, misPatientId, planSummary.plan_id, staffUserId])

  const title = (planSummary.plan_title ?? '').trim() || 'План лечения'

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/45 p-4 pt-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mis-plan-correction-title"
    >
      <div className="mb-8 w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div className="min-w-0">
            <h2 id="mis-plan-correction-title" className="text-base font-semibold text-slate-900">
              Корректировка ИИ-плана
            </h2>
            <p className="mt-0.5 truncate text-xs text-slate-600">
              {misImportedPlanDateLabel(planSummary)} · {title} · {misImportedPlanDoctorLabel(planSummary)}
            </p>
            <p className="mt-1 text-[11px] text-violet-800">
              Уточните текстом или голосом, пересоберите по matcher или сохраните JSON — затем «Применить» создаст
              новый план в «Планы МИС пациента».
            </p>
          </div>
          <button
            type="button"
            className="shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            onClick={onClose}
            aria-label="Закрыть"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="max-h-[calc(100vh-8rem)] overflow-y-auto p-4">
          {loading ? (
            <p className="py-6 text-center text-sm text-slate-500">Загружаем план и готовим черновик…</p>
          ) : error ? (
            <div className="space-y-3">
              <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>
              <button type="button" className="rounded-md border border-slate-300 px-3 py-1.5 text-xs" onClick={onClose}>
                Закрыть
              </button>
            </div>
          ) : proposal ? (
            <PlanDiagnosticsReviewPanel
              apiBase={apiBase}
              session={session}
              misPatientId={misPatientId}
              staffUserId={staffUserId}
              uiSurface={UI_SURFACE}
              proposal={proposal}
              correctionMode
              confirmLabel="Применить скорректированный план в Планы МИС"
              onProposalChange={setProposal}
              onApplied={(data) => {
                onApplied(extractMisPlanId(data))
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}
